import { supabase } from './supabase';

function unwrapSingle(result) {
  const { data, error } = result;
  if (error) throw error;
  if (Array.isArray(data)) return data[0] ?? null;
  return data ?? null;
}

function unwrapList(result) {
  const { data, error } = result;
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

function toNumber(value, fallback = 0) {
  if (value === '' || value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toInteger(value, fallback = null) {
  if (value === '' || value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function toJsonArray(value, fallback = []) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return trimmed
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return fallback;
}

async function rpc(name, params = {}) {
  return supabase.rpc(name, params);
}

export const spformsApi = {
  slugify(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  },

  // --- GLOBAL SETTINGS (SUPERADMIN ONLY) ---

  async settingsGetPublic() {
    const data = await unwrapList(await rpc('spforms_settings_list_public'));
    return data.reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {});
  },

  async settingsGetAll(token) {
    return unwrapList(await rpc('spforms_settings_get_all', { p_token: token }));
  },

  async settingsUpsert(token, key, value, isPublic = false) {
    const { error } = await rpc('spforms_settings_upsert', {
      p_token: token,
      p_key: key,
      p_value: String(value),
      p_is_public: isPublic,
    });
    if (error) throw error;
    return true;
  },

  // --- AUTH ---

  async authLogin(email, password, recaptchaToken = null, recaptchaAction = null) {
    return unwrapSingle(
      await rpc('spforms_auth_login', {
        p_email: String(email || '').trim().toLowerCase(),
        p_password: String(password || ''),
        p_recaptcha_token: recaptchaToken,
        p_recaptcha_action: recaptchaAction,
      })
    );
  },

  async authValidateSession(token) {
    return unwrapSingle(
      await rpc('spforms_auth_validate_session', {
        p_token: String(token || '').trim(),
      })
    );
  },

  async authLogout(token) {
    const { error } = await rpc('spforms_auth_logout', {
      p_token: String(token || '').trim(),
    });
    if (error) throw error;
    return true;
  },

  // --- INTAKE GROUPS ---

  async intakeGroupsList(tenantId) {
    return unwrapList(
      await rpc('spforms_intake_groups_list', {
        p_tenant_id: tenantId,
      })
    );
  },

  async intakeGroupCreate(payload) {
    return unwrapSingle(
      await rpc('spforms_intake_group_create', {
        p_tenant_id: payload.tenant_id,
        p_title: payload.title,
        p_type: payload.type,
        p_start_date: payload.start_date || null,
        p_end_date: payload.end_date || null,
        p_location: payload.location || null,
        p_capacity: toInteger(payload.capacity),
        p_status: payload.status || 'active',
        p_public_slug: payload.public_slug,
        p_notes: payload.notes || null,
        p_price_amount: toNumber(payload.price_amount, 0),
        p_early_bird_enabled: Boolean(payload.early_bird_enabled),
        p_early_bird_until: payload.early_bird_until || null,
        p_early_bird_price:
          payload.early_bird_price === '' || payload.early_bird_price === null
            ? null
            : toNumber(payload.early_bird_price, 0),
        p_jersey_size_options: toJsonArray(payload.jersey_size_options, [
          '8Y',
          '10Y',
          '12Y',
          '14Y',
          'XS',
          'S',
          'M',
          'L',
          'XL',
          'XXL',
        ]),
        p_banner_source_type: payload.banner_source_type || 'url',
        p_banner_image_url: payload.banner_image_url || null,
        p_banner_storage_path: payload.banner_storage_path || null,

        p_wire_transfer_enabled: Boolean(payload.wire_transfer_enabled),
        p_wire_transfer_iban: payload.wire_transfer_iban || null,
        p_wire_transfer_beneficiary: payload.wire_transfer_beneficiary || null,
        p_wire_transfer_bank_name: payload.wire_transfer_bank_name || null,
        p_wire_transfer_notes: payload.wire_transfer_notes || null,

        p_wire_transfer_2_enabled: Boolean(payload.wire_transfer_2_enabled),
        p_wire_transfer_2_iban: payload.wire_transfer_2_iban || null,
        p_wire_transfer_2_beneficiary: payload.wire_transfer_2_beneficiary || null,
        p_wire_transfer_2_bank_name: payload.wire_transfer_2_bank_name || null,
        p_wire_transfer_2_notes: payload.wire_transfer_2_notes || null,
      })
    );
  },

  async intakeGroupUpdate(payload) {
    const { error } = await rpc('spforms_intake_group_update', {
      p_id: payload.id,
      p_tenant_id: payload.tenant_id,
      p_title: payload.title,
      p_type: payload.type,
      p_start_date: payload.start_date || null,
      p_end_date: payload.end_date || null,
      p_location: payload.location || null,
      p_capacity: toInteger(payload.capacity),
      p_status: payload.status || 'active',
      p_public_slug: payload.public_slug,
      p_public_enabled: Boolean(payload.public_enabled),
      p_notes: payload.notes || null,
      p_price_amount: toNumber(payload.price_amount, 0),
      p_early_bird_enabled: Boolean(payload.early_bird_enabled),
      p_early_bird_until: payload.early_bird_until || null,
      p_early_bird_price:
        payload.early_bird_price === '' || payload.early_bird_price === null
          ? null
          : toNumber(payload.early_bird_price, 0),
      p_jersey_size_options: toJsonArray(payload.jersey_size_options, [
        '8Y',
        '10Y',
        '12Y',
        '14Y',
        'XS',
        'S',
        'M',
        'L',
        'XL',
        'XXL',
      ]),
      p_banner_source_type: payload.banner_source_type || 'url',
      p_banner_image_url: payload.banner_image_url || null,
      p_banner_storage_path: payload.banner_storage_path || null,

      p_wire_transfer_enabled: Boolean(payload.wire_transfer_enabled),
      p_wire_transfer_iban: payload.wire_transfer_iban || null,
      p_wire_transfer_beneficiary: payload.wire_transfer_beneficiary || null,
      p_wire_transfer_bank_name: payload.wire_transfer_bank_name || null,
      p_wire_transfer_notes: payload.wire_transfer_notes || null,

      p_wire_transfer_2_enabled: Boolean(payload.wire_transfer_2_enabled),
      p_wire_transfer_2_iban: payload.wire_transfer_2_iban || null,
      p_wire_transfer_2_beneficiary: payload.wire_transfer_2_beneficiary || null,
      p_wire_transfer_2_bank_name: payload.wire_transfer_2_bank_name || null,
      p_wire_transfer_2_notes: payload.wire_transfer_2_notes || null,
    });

    if (error) throw error;
    return true;
  },

  async intakeGroupDelete(id, tenantId) {
    const { error } = await rpc('spforms_intake_group_delete', {
      p_id: id,
      p_tenant_id: tenantId,
    });
    if (error) throw error;
    return true;
  },

  // --- FORM FIELDS ---

  async formFieldsList(tenantId, intakeGroupId) {
    return unwrapList(
      await rpc('spforms_form_fields_list', {
        p_tenant_id: tenantId,
        p_intake_group_id: intakeGroupId,
      })
    );
  },

  async seedDefaultFields(tenantId, intakeGroupId) {
    const { error } = await rpc('spforms_form_fields_seed_defaults', {
      p_tenant_id: tenantId,
      p_intake_group_id: intakeGroupId,
    });
    if (error) throw error;
    return true;
  },

  async formFieldCreate(payload) {
    return unwrapSingle(
      await rpc('spforms_form_field_create', {
        p_tenant_id: payload.tenant_id,
        p_intake_group_id: payload.intake_group_id,
        p_field_key: payload.field_key,
        p_label: payload.label,
        p_help_text: payload.help_text || null,
        p_field_type: payload.field_type || 'text',
        p_options_json: toJsonArray(payload.options_json),
        p_required: Boolean(payload.required),
        p_active: payload.active !== false,
        p_sort_order: toInteger(payload.sort_order, 0),
        p_maps_to_core_field: payload.maps_to_core_field || null,
      })
    );
  },

  async formFieldUpdate(payload) {
    const { error } = await rpc('spforms_form_field_update', {
      p_id: payload.id,
      p_tenant_id: payload.tenant_id,
      p_intake_group_id: payload.intake_group_id,
      p_field_key: payload.field_key,
      p_label: payload.label,
      p_help_text: payload.help_text || null,
      p_field_type: payload.field_type || 'text',
      p_options_json: toJsonArray(payload.options_json),
      p_required: Boolean(payload.required),
      p_active: payload.active !== false,
      p_sort_order: toInteger(payload.sort_order, 0),
      p_maps_to_core_field: payload.maps_to_core_field || null,
    });
    if (error) throw error;
    return true;
  },

  async formFieldDelete(tenantId, fieldId) {
    const { error } = await rpc('spforms_form_field_delete', {
      p_tenant_id: tenantId,
      p_field_id: fieldId,
    });
    if (error) throw error;
    return true;
  },

  // --- DISCOUNT CODES ---

  async discountCodesList(tenantId, intakeGroupId) {
    return unwrapList(
      await rpc('spforms_discount_codes_list', {
        p_tenant_id: tenantId,
        p_intake_group_id: intakeGroupId,
      })
    );
  },

  async discountCodeCreate(payload) {
    return unwrapSingle(
      await rpc('spforms_discount_code_create', {
        p_tenant_id: payload.tenant_id,
        p_intake_group_id: payload.intake_group_id,
        p_code: String(payload.code || '').trim().toUpperCase(),
        p_discount_type: payload.discount_type || 'fixed',
        p_amount: toNumber(payload.amount, 0),
        p_active: payload.active !== false,
        p_notes: payload.notes || null,
      })
    );
  },

  async discountCodeUpdate(payload) {
    const { error } = await rpc('spforms_discount_code_update', {
      p_id: payload.id,
      p_tenant_id: payload.tenant_id,
      p_intake_group_id: payload.intake_group_id,
      p_code: String(payload.code || '').trim().toUpperCase(),
      p_discount_type: payload.discount_type || 'fixed',
      p_amount: toNumber(payload.amount, 0),
      p_active: payload.active !== false,
      p_notes: payload.notes || null,
    });
    if (error) throw error;
    return true;
  },

  async discountCodeDelete(tenantId, codeId) {
    const { error } = await rpc('spforms_discount_code_delete', {
      p_tenant_id: tenantId,
      p_discount_code_id: codeId,
    });
    if (error) throw error;
    return true;
  },

  // --- PUBLIC FORM / REGISTRATION ---

  async publicFormGet(publicSlug) {
    return unwrapSingle(
      await rpc('spforms_public_form_get', {
        p_public_slug: publicSlug,
      })
    );
  },

  async registrationCreatePublic(payload) {
    return unwrapSingle(
      await rpc('spforms_registration_create_public', {
        p_public_slug: payload.public_slug,
        p_core: payload.core || {},
        p_answers: payload.answers || {},
        p_discount_code: payload.discount_code || null,
        p_recaptcha_token: payload.recaptcha_token || null,
        p_recaptcha_action: payload.recaptcha_action || null,
      })
    );
  },

  // --- PARTICIPANTS / PAYMENTS ---

  async registrationsList(tenantId, intakeGroupId) {
    return unwrapList(
      await rpc('spforms_registrations_list', {
        p_tenant_id: tenantId,
        p_intake_group_id: intakeGroupId,
      })
    );
  },

  async participantsList(tenantId, intakeGroupId) {
    return unwrapList(
      await rpc('spforms_participants_list', {
        p_tenant_id: tenantId,
        p_intake_group_id: intakeGroupId,
      })
    );
  },

  async participantApprove(registrationId, tenantId) {
    return unwrapSingle(
      await rpc('spforms_participant_approve', {
        p_registration_id: registrationId,
        p_tenant_id: tenantId,
      })
    );
  },

  async participantPaymentUpdate(payload) {
    const { error } = await rpc('spforms_participant_payment_update', {
      p_tenant_id: payload.tenant_id,
      p_participant_id: payload.participant_id,
      p_payment_status: payload.payment_status,
      p_amount_due: toNumber(payload.amount_due, 0),
      p_amount_paid: toNumber(payload.amount_paid, 0),
      p_payment_gateway: payload.payment_gateway || null,
    });
    if (error) throw error;
    return true;
  },

  // --- EMAIL TEMPLATES / CUSTOM MESSAGES ---

  async emailTemplatesList(token, tenantId, intakeGroupId = null) {
    return unwrapList(
      await rpc('spforms_email_templates_list', {
        p_token: String(token || '').trim(),
        p_tenant_id: tenantId,
        p_intake_group_id: intakeGroupId,
      })
    );
  },

  async emailTemplateCreate(payload) {
  if (!payload?.token) throw new Error('Missing session token');
  if (!payload?.tenant_id) throw new Error('Missing tenant_id');
  if (!payload?.title) throw new Error('Missing template title');
  if (!payload?.subject) throw new Error('Missing template subject');
  if (!payload?.body) throw new Error('Missing template body');

  return unwrapSingle(
    await rpc('spforms_email_template_create_v2', {
      p_token: String(payload.token || '').trim(),
      p_tenant_id: payload.tenant_id,
      p_intake_group_id: payload.intake_group_id || null,
      p_title: payload.title,
      p_subject: payload.subject,
      p_body: payload.body,
      p_template_type: payload.template_type || 'custom',
      p_active: payload.active !== false,
    })
  );
},

  async emailTemplateUpdate(payload) {
    return unwrapSingle(
      await rpc('spforms_email_template_update', {
        p_token: String(payload.token || '').trim(),
        p_id: payload.id,
        p_tenant_id: payload.tenant_id,
        p_intake_group_id: payload.intake_group_id || null,
        p_title: payload.title,
        p_subject: payload.subject,
        p_body: payload.body,
        p_template_type: payload.template_type || 'custom',
        p_active: payload.active !== false,
      })
    );
  },

  async emailTemplateDelete(token, templateId, tenantId) {
    const { error } = await rpc('spforms_email_template_delete', {
      p_token: String(token || '').trim(),
      p_id: templateId,
      p_tenant_id: tenantId,
    });
    if (error) throw error;
    return true;
  },

  async emailLogsList(token, tenantId, filters = {}) {
    return unwrapList(
      await rpc('spforms_email_logs_list', {
        p_token: String(token || '').trim(),
        p_tenant_id: tenantId,
        p_intake_group_id: filters.intake_group_id || null,
        p_registration_id: filters.registration_id || null,
        p_participant_id: filters.participant_id || null,
      })
    );
  },

  async emailLogCreate(payload) {
    return unwrapSingle(
      await rpc('spforms_email_log_create', {
        p_token: String(payload.token || '').trim(),
        p_tenant_id: payload.tenant_id,
        p_intake_group_id: payload.intake_group_id || null,
        p_registration_id: payload.registration_id || null,
        p_participant_id: payload.participant_id || null,
        p_template_id: payload.template_id || null,
        p_recipient_email: payload.recipient_email || '',
        p_recipient_name: payload.recipient_name || '',
        p_subject: payload.subject || '',
        p_body: payload.body || '',
        p_email_type: payload.email_type || 'custom',
        p_status: payload.status || 'sent',
        p_provider: payload.provider || 'resend',
        p_provider_response: payload.provider_response || null,
        p_error_message: payload.error_message || null,
      })
    );
  },

  // --- FINANCES ---

  async eventFinanceSummary(tenantId, intakeGroupId) {
    return unwrapSingle(
      await rpc('spforms_event_finance_summary', {
        p_tenant_id: tenantId,
        p_intake_group_id: intakeGroupId,
      })
    );
  },

  async eventExpensesList(tenantId, intakeGroupId) {
    return unwrapList(
      await rpc('spforms_event_expenses_list', {
        p_tenant_id: tenantId,
        p_intake_group_id: intakeGroupId,
      })
    );
  },

  async eventExpenseCreate(payload) {
    return unwrapSingle(
      await rpc('spforms_event_expense_create', {
        p_tenant_id: payload.tenant_id,
        p_intake_group_id: payload.intake_group_id,
        p_title: payload.title,
        p_category: payload.category || 'other',
        p_amount: toNumber(payload.amount, 0),
        p_notes: payload.notes || null,
        p_expense_date: payload.expense_date || null,
      })
    );
  },

  async eventExpenseUpdate(payload) {
    const { error } = await rpc('spforms_event_expense_update', {
      p_tenant_id: payload.tenant_id,
      p_expense_id: payload.id,
      p_title: payload.title,
      p_category: payload.category || 'other',
      p_amount: toNumber(payload.amount, 0),
      p_notes: payload.notes || null,
      p_expense_date: payload.expense_date || null,
    });
    if (error) throw error;
    return true;
  },

  async eventExpenseDelete(tenantId, expenseId) {
    const { error } = await rpc('spforms_event_expense_delete', {
      p_tenant_id: tenantId,
      p_expense_id: expenseId,
    });
    if (error) throw error;
    return true;
  },

  // --- ADMIN ---

  async adminTenantsList() {
    return unwrapList(await rpc('spforms_admin_tenants_list'));
  },

  async adminTenantCreateWithAdmin(payload) {
    return unwrapSingle(
      await rpc('spforms_admin_tenant_create_with_admin', {
        p_name: payload.name,
        p_slug: payload.slug,
        p_contact_name: payload.contact_name || null,
        p_contact_email: payload.contact_email || null,
        p_contact_phone: payload.contact_phone || null,
        p_notes: payload.notes || null,
        p_admin_name: payload.admin_name,
        p_admin_email: payload.admin_email,
        p_admin_password: payload.admin_password,
      })
    );
  },

  async adminTenantUpdate(payload) {
    const { error } = await rpc('spforms_admin_tenant_update', {
      p_id: payload.id,
      p_name: payload.name,
      p_slug: payload.slug,
      p_status: payload.status,
      p_contact_name: payload.contact_name || null,
      p_contact_email: payload.contact_email || null,
      p_contact_phone: payload.contact_phone || null,
      p_notes: payload.notes || null,
      p_smtp_enabled: Boolean(payload.smtp_enabled),
      p_smtp_duplicate_confirmation_enabled: Boolean(
        payload.smtp_duplicate_confirmation_enabled
      ),
      p_smtp_host: payload.smtp_host || null,
      p_smtp_port: toInteger(payload.smtp_port, null),
      p_smtp_secure: payload.smtp_secure !== false,
      p_smtp_username: payload.smtp_username || null,
      p_smtp_password: payload.smtp_password || null,
      p_smtp_from_name: payload.smtp_from_name || null,
      p_smtp_from_email: payload.smtp_from_email || null,
      p_smtp_reply_to_email: payload.smtp_reply_to_email || null,
      p_email_delivery_approval: payload.email_delivery_approval || 'default_only',
      p_email_delivery_payment: payload.email_delivery_payment || 'default_only',
      p_email_delivery_custom: payload.email_delivery_custom || 'default_only',
      p_email_delivery_bulk_custom: payload.email_delivery_bulk_custom || 'default_only',
    });
    if (error) throw error;
    return true;
  },

  async adminUsersList(tenantId) {
    return unwrapList(
      await rpc('spforms_admin_users_list', {
        p_tenant_id: tenantId,
      })
    );
  },

  async adminUserCreate(payload) {
    return unwrapSingle(
      await rpc('spforms_admin_user_create', {
        p_tenant_id: payload.tenant_id,
        p_name: payload.name,
        p_email: payload.email,
        p_password: payload.password,
        p_role: payload.role || 'tenant_admin',
        p_status: payload.status || 'active',
      })
    );
  },

  async calculatePrice(intakeGroupId, discountCode = '') {
    return unwrapSingle(
      await rpc('spforms_calculate_registration_price', {
        p_intake_group_id: intakeGroupId,
        p_discount_code: discountCode || null,
      })
    );
  },

  async sendRegistrationConfirmationEmail(payload) {
    const { data, error } = await supabase.functions.invoke(
      'spforms-send-confirmation-email',
      {
        body: {
          email_type: payload.email_type || 'confirmation',
          public_slug: payload.public_slug || '',
          registration_id: payload.registration_id || null,
          participant_id: payload.participant_id || null,
          core: payload.core || {},
          answers: payload.answers || {},
          discount_code: payload.discount_code || null,
          final_price: toNumber(payload.final_price, 0),
          amount_due: toNumber(payload.amount_due, payload.final_price || 0),
          amount_paid: toNumber(payload.amount_paid, 0),
          new_payment_amount: toNumber(payload.new_payment_amount, 0),
          remaining_balance: toNumber(payload.remaining_balance, 0),
          payment_status: payload.payment_status || '',
          payment_gateway: payload.payment_gateway || '',
          group: payload.group || {},
          smtp_settings: payload.smtp_settings || null,
        },
      }
    );

    if (error) {
      console.error('Registration email failed:', error);
      throw error;
    }

    if (data?.success === false) {
      console.error('Registration email failed:', data);
      throw new Error(
        data?.error || JSON.stringify(data) || 'Registration email failed'
      );
    }

    return data || true;
  },

  async sendCustomEmail(payload) {
    const { data, error } = await supabase.functions.invoke(
      'spforms-send-confirmation-email',
      {
        body: {
          email_type: 'custom',

          tenant_id: payload.tenant_id || null,
          tenant_name: payload.tenant_name || '',
          tenant_email: payload.tenant_email || '',

          public_slug: payload.public_slug || '',
          intake_group_id: payload.intake_group_id || null,
          registration_id: payload.registration_id || null,
          participant_id: payload.participant_id || null,

          recipient_email: payload.recipient_email || '',
          participant_name: payload.participant_name || '',

          custom_subject: payload.custom_subject || payload.subject || '',
          custom_body: payload.custom_body || payload.body || '',

          payment_email_subject:
            payload.payment_email_subject || 'Συμμετοχή Tryouts 26',

          core: payload.core || {},
          answers: payload.answers || {},
          group: payload.group || {},

          final_price: toNumber(payload.final_price, 0),
          amount_due: toNumber(payload.amount_due, payload.final_price || 0),
          amount_paid: toNumber(payload.amount_paid, 0),
          remaining_balance: toNumber(payload.remaining_balance, 0),
          outstanding_balance: toNumber(
            payload.outstanding_balance,
            payload.remaining_balance || 0
          ),
          payment_status: payload.payment_status || '',
          payment_gateway: payload.payment_gateway || '',
          discount_code: payload.discount_code || null,
          smtp_settings: payload.smtp_settings || null,
        },
      }
    );

    if (error) {
      console.error('Custom email failed:', error);
      throw error;
    }

    if (data?.success === false) {
      console.error('Custom email failed:', data);
      throw new Error(data?.error || JSON.stringify(data) || 'Custom email failed');
    }

    return data || true;
  },

  async sendBulkCustomEmail(payload) {
    const { data, error } = await supabase.functions.invoke(
      'spforms-send-confirmation-email',
      {
        body: {
          email_type: 'bulk_custom',

          tenant_id: payload.tenant_id || null,
          tenant_name: payload.tenant_name || '',
          tenant_email: payload.tenant_email || '',

          public_slug: payload.public_slug || '',
          intake_group_id: payload.intake_group_id || null,

          custom_subject: payload.custom_subject || payload.subject || '',
          custom_body: payload.custom_body || payload.body || '',

          payment_email_subject:
            payload.payment_email_subject || 'Συμμετοχή Tryouts 26',

          group: payload.group || {},
          recipients: Array.isArray(payload.recipients) ? payload.recipients : [],
          smtp_settings: payload.smtp_settings || null,
        },
      }
    );

    if (error) {
      console.error('Bulk custom email failed:', error);
      throw error;
    }

    if (data?.success === false && Number(data?.sent || 0) === 0) {
      console.error('Bulk custom email failed:', data);
      throw new Error(
        data?.error || JSON.stringify(data) || 'Bulk custom email failed'
      );
    }

    return data || true;
  },

  async registrationUpdateStatus(id, status, tenantId) {
    const { error } = await rpc('spforms_registration_update_status', {
      p_id: id,
      p_status: status,
      p_tenant_id: tenantId,
    });
    if (error) throw error;
    return true;
  },

  async uploadPublicAsset({ tenantId, intakeGroupId, file, folder = 'answers' }) {
    if (!file) return null;
    const timestamp = new Date().toISOString().replace(/[^0-9]/g, '');
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const filePath = `${tenantId}/${intakeGroupId}/${folder}/${timestamp}_${cleanFileName}`;

    const { error } = await supabase.storage
      .from('spforms-uploads')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (error) throw error;

    const {
      data: { publicUrl },
    } = supabase.storage.from('spforms-uploads').getPublicUrl(filePath);

    return { url: publicUrl, path: filePath };
  },

  async eventIncomeList(tenantId, intakeGroupId) {
    const { data, error } = await supabase.rpc('spforms_event_income_list', {
      p_tenant_id: tenantId,
      p_intake_group_id: intakeGroupId,
    });
    if (error) throw error;
    return data || [];
  },

  async eventIncomeCreate(payload) {
    const { data, error } = await supabase.rpc('spforms_event_income_create', {
      p_tenant_id: payload.tenant_id,
      p_intake_group_id: payload.intake_group_id,
      p_title: payload.title,
      p_category: payload.category || 'other',
      p_amount: Number(payload.amount || 0),
      p_notes: payload.notes || null,
      p_income_date: payload.income_date || null,
    });
    if (error) throw error;
    return data;
  },

  async eventIncomeUpdate(payload) {
    const { data, error } = await supabase.rpc('spforms_event_income_update', {
      p_tenant_id: payload.tenant_id,
      p_income_id: payload.id,
      p_title: payload.title,
      p_category: payload.category || 'other',
      p_amount: Number(payload.amount || 0),
      p_notes: payload.notes || null,
      p_income_date: payload.income_date || null,
    });
    if (error) throw error;
    return data;
  },

  async eventIncomeDelete(tenantId, incomeId) {
    const { data, error } = await supabase.rpc('spforms_event_income_delete', {
      p_tenant_id: tenantId,
      p_income_id: incomeId,
    });
    if (error) throw error;
    return data;
  },
};

export default spformsApi;